import { supabase } from "@/lib/supabaseClient";
import type {
  EmployeeSelfServiceAbsence,
  EmployeeSelfServiceLeaveRequest,
  EmployeeSelfServiceTimeDispute,
  EmployeeSelfServiceTimesheetEntry,
} from "./mappers";
import { assertOk, toBoolean, toNumber, type RpcResult } from "./mappers";

export async function listMyTimesheetEntries(params: {
  companyId: string;
  startDate?: string;
  endDate?: string;
}): Promise<EmployeeSelfServiceTimesheetEntry[]> {
  const res = (await supabase.rpc("list_my_timesheet_entries", {
    p_company_id: params.companyId,
    p_start_date: params.startDate ?? null,
    p_end_date: params.endDate ?? null,
  })) as RpcResult<EmployeeSelfServiceTimesheetEntry[]>;

  return assertOk(res).map((row: any) => ({
    log_id: String(row.log_id),
    project_id: row.project_id == null ? null : String(row.project_id),
    work_date: String(row.work_date),
    entry_time: row.entry_time == null ? null : String(row.entry_time),
    exit_time: row.exit_time == null ? null : String(row.exit_time),
    total_hours: row.total_hours == null ? null : toNumber(row.total_hours),
    source_type: row.source_type == null ? null : String(row.source_type),
    is_public_holiday:
      row.is_public_holiday == null ? null : toBoolean(row.is_public_holiday),
    status: row.status == null ? null : String(row.status),
    has_shadowing_anomaly:
      row.has_shadowing_anomaly == null ? null : toBoolean(row.has_shadowing_anomaly),
    daily_ot: row.daily_ot == null ? null : toNumber(row.daily_ot),
    daily_reg: row.daily_reg == null ? null : toNumber(row.daily_reg),
    week_start_date:
      row.week_start_date == null ? null : String(row.week_start_date),
    weekly_ot_carry:
      row.weekly_ot_carry == null ? null : toNumber(row.weekly_ot_carry),
    integrity_alert:
      row.integrity_alert == null ? null : toBoolean(row.integrity_alert),
  }));
}

export async function listMyTimeDisputes(
  companyId: string
): Promise<EmployeeSelfServiceTimeDispute[]> {
  const res = (await supabase.rpc("list_my_time_disputes", {
    p_company_id: companyId,
  })) as RpcResult<EmployeeSelfServiceTimeDispute[]>;

  return assertOk(res).map((row: any) => ({
    id: String(row.id),
    work_date: String(row.work_date),
    proposed_hours: toNumber(row.proposed_hours),
    reason: String(row.reason),
    status: row.status == null ? null : String(row.status),
    manager_feedback:
      row.manager_feedback == null ? null : String(row.manager_feedback),
    created_at: row.created_at == null ? null : String(row.created_at),
  }));
}

export async function submitMyTimeDispute(params: {
  companyId: string;
  workDate: string;
  proposedHours: number;
  reason: string;
}): Promise<string> {
  const res = (await supabase.rpc("submit_my_time_dispute", {
    p_company_id: params.companyId,
    p_work_date: params.workDate,
    p_proposed_hours: params.proposedHours,
    p_reason: params.reason,
  })) as RpcResult<string>;

  return assertOk(res);
}

export async function cancelMyTimeDispute(params: {
  disputeId: string;
  cancellationReason: string;
}): Promise<{
  dispute_id: string;
  company_id: string;
  employee_id: string;
  status: string;
  cancellation_event_id: string;
}> {
  const res = (await supabase.rpc("cancel_my_time_dispute", {
    p_dispute_id: params.disputeId,
    p_cancellation_reason: params.cancellationReason,
  })) as RpcResult<{
    dispute_id: string;
    company_id: string;
    employee_id: string;
    status: string;
    cancellation_event_id: string;
  }>;

  return assertOk(res);
}

export async function listMyAbsences(
  companyId: string
): Promise<EmployeeSelfServiceAbsence[]> {
  const res = (await supabase.rpc("list_my_absences", {
    p_company_id: companyId,
  })) as RpcResult<EmployeeSelfServiceAbsence[]>;

  return assertOk(res).map((row: any) => ({
    id: String(row.id),
    type: row.type == null ? null : String(row.type),
    start_date: String(row.start_date),
    end_date: String(row.end_date),
    status: row.status == null ? null : String(row.status),
    certification_state:
      row.certification_state == null ? null : String(row.certification_state),
    physical_original_required: toBoolean(row.physical_original_required),
    physical_original_status:
      row.physical_original_status == null
        ? "not_required"
        : String(row.physical_original_status),
    employee_note:
      row.employee_note == null ? null : String(row.employee_note),
    manager_feedback:
      row.manager_feedback == null ? null : String(row.manager_feedback),
    created_at: row.created_at == null ? null : String(row.created_at),
  }));
}

export async function reportMyAbsence(params: {
  companyId: string;
  type: "other" | "maternity_paternity";
  startDate: string;
  endDate: string;
  employeeNote?: string;
}): Promise<string> {
  const res = (await supabase.rpc("report_my_absence", {
    p_company_id: params.companyId,
    p_type: params.type,
    p_start_date: params.startDate,
    p_end_date: params.endDate,
    p_employee_note: params.employeeNote ?? null,
  })) as RpcResult<string>;

  return assertOk(res);
}

export async function reportMySickAbsence(params: {
  companyId: string;
  startDate: string;
  endDate: string;
  employeeNote?: string;
}): Promise<string> {
  const res = (await supabase.rpc("report_my_sick_absence", {
    p_company_id: params.companyId,
    p_start_date: params.startDate,
    p_end_date: params.endDate,
    p_employee_note: params.employeeNote ?? null,
  })) as RpcResult<string>;

  return assertOk(res);
}

export async function listMyLeaveRequests(
  companyId: string
): Promise<EmployeeSelfServiceLeaveRequest[]> {
  const res = (await supabase.rpc("list_my_leave_requests", {
    p_company_id: companyId,
  })) as RpcResult<EmployeeSelfServiceLeaveRequest[]>;

  return assertOk(res).map((row: any) => ({
    id: String(row.id),
    type: row.type == null ? null : String(row.type),
    start_date: String(row.start_date),
    end_date: String(row.end_date),
    status: row.status == null ? null : String(row.status),
    employee_note:
      row.employee_note == null ? null : String(row.employee_note),
    manager_feedback:
      row.manager_feedback == null ? null : String(row.manager_feedback),
    created_at: row.created_at == null ? null : String(row.created_at),
  }));
}

export async function requestMyLeave(params: {
  companyId: string;
  type: "vacation" | "personal_time_off";
  startDate: string;
  endDate: string;
  employeeNote?: string;
}): Promise<string> {
  const res = (await supabase.rpc("request_my_leave", {
    p_company_id: params.companyId,
    p_type: params.type,
    p_start_date: params.startDate,
    p_end_date: params.endDate,
    p_employee_note: params.employeeNote ?? null,
  })) as RpcResult<string>;

  return assertOk(res);
}